
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useBusinessParameters } from '@/hooks/useParameters';

interface ImageGalleryProps {
  images: string[];
  alt?: string;
  className?: string;
  aspectRatio?: 'square' | 'video' | 'wide' | 'auto';
  fallbackImage?: string;
  logoUrl?: string;
  showLogo?: boolean;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  alt = 'Gallery image',
  className,
  aspectRatio = 'wide',
  fallbackImage,
  logoUrl,
  showLogo = false
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { defaultBannerUrl } = useBusinessParameters();
  const filteredImages = images.filter(img => img);
  const hasImages = filteredImages.length > 0;
  
  const aspectRatioClass = {
    square: 'aspect-square',
    video: 'aspect-video',
    wide: 'aspect-[16/9]',
    auto: ''
  };

  // Use the provided fallbackImage, or the default banner from parameters
  const effectiveFallback = fallbackImage || defaultBannerUrl;

  const nextImage = () => {
    setCurrentIndex((prev) => (prev === filteredImages.length - 1 ? 0 : prev + 1));
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev === 0 ? filteredImages.length - 1 : prev - 1));
  };

  // Logo component that we'll show in the corner if provided
  const LogoOverlay = () => {
    if (!showLogo || !logoUrl) return null;
    
    return (
      <div className="absolute left-3 bottom-3 z-20">
        <div className="h-12 w-12 rounded-full overflow-hidden border-2 border-white shadow-md">
          <img 
            src={logoUrl} 
            alt="Company logo" 
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    );
  };

  if (!hasImages) {
    return (
      <div className={cn('relative w-full h-full overflow-hidden rounded-lg', aspectRatioClass[aspectRatio], className)}>
        <img
          src={effectiveFallback}
          alt="No image available"
          className="w-full h-full object-cover"
        />
        <LogoOverlay />
      </div>
    );
  }

  if (filteredImages.length === 1) {
    return (
      <div className={cn('relative w-full h-full overflow-hidden rounded-lg', aspectRatioClass[aspectRatio], className)}>
        <img
          src={filteredImages[0]}
          alt={alt}
          className="w-full h-full object-cover"
        />
        <LogoOverlay />
      </div>
    );
  }

  return (
    <div className={cn('relative w-full h-full overflow-hidden rounded-lg', aspectRatioClass[aspectRatio], className)}>
      <AnimatePresence mode="wait">
        <motion.img
          key={currentIndex}
          src={filteredImages[currentIndex]}
          alt={`${alt} ${currentIndex + 1}`}
          className="w-full h-full object-cover"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        />
      </AnimatePresence>
      
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
      
      <LogoOverlay />
      
      <button
        onClick={prevImage}
        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white backdrop-blur-sm rounded-full p-2 shadow-md transition-all"
        aria-label="Previous image"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      
      <button
        onClick={nextImage}
        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white backdrop-blur-sm rounded-full p-2 shadow-md transition-all"
        aria-label="Next image"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
        {filteredImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={cn(
              "w-2 h-2 rounded-full transition-all",
              index === currentIndex ? "bg-white scale-110" : "bg-white/50"
            )}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageGallery;
